import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from '@redux/hooks';
import {
  getClusterLicenseValidity,
  getReflectorLicenseValidity,
  getReflectorsList,
  type ReflectorLicenseValidity
} from '../Reflector/request/request';

/** What a gated view needs to render its card. `null` means "render ungated". */
export interface LicenseGate {
  /** How to name the target in the copy. */
  subject: string;
  reason: string;
  endDate: string | null;
}

type LicenseGateScope =
  /** `id` is the reflector's numeric database id. */
  | { scope: 'instance'; id?: number | string | null }
  /**
   * `id` is the reflector's `subId` (UUID). BGP Visibility addresses reflectors this way
   * because its documents live in S3 under `${tenantId}/${subId}/${direction}.json`, so the
   * numeric id never reaches that view. Resolved client-side against the reflectors list
   * rather than by widening the backend, which would leak the numeric identity into a
   * subsystem that deliberately uses `subId`.
   */
  | { scope: 'instanceBySubId'; id?: string | null }
  | { scope: 'cluster'; id?: number | string | null };

/** "Primary CTXR-5cb248" when a role is known, otherwise just the reflector's name. */
const memberSubject = (member: ReflectorLicenseValidity): string => {
  const name = member.name || member.subId || `Reflector ${member.reflectorId}`;
  if (!member.role) {
    return name;
  }
  const role = member.role.charAt(0).toUpperCase() + member.role.slice(1);
  return `${role} ${name}`;
};

/**
 * Names the members holding a cluster back. A cluster is usable only while every member is
 * licensed, so more than one can be at fault and the operator needs to know which licenses to
 * chase — naming only the first would send them to renew half of what is broken.
 */
const clusterSubject = (members: ReflectorLicenseValidity[]): string => {
  const offenders = members.filter(member => !member.valid);
  if (offenders.length === 0) {
    return 'This cluster';
  }
  return offenders.map(memberSubject).join(' and ');
};

/**
 * Resolves whether a reflector's or cluster's views must be license-gated (CTX-346).
 *
 * Validity comes from the CTX-345 endpoints, never from the `license` object embedded in
 * reflector rows: a client-side date comparison drifts from what the API actually enforces.
 *
 * **Fails open.** An error, an absent payload or a missing id all render ungated — a backend
 * blip must not black out four working views, and the gate is presentation anyway (the API is
 * what actually refuses writes).
 *
 * The request is keyed on the id alone, deliberately **not** on which tab is open, so switching
 * between BGP / CPR / Syslog inside one detail page issues no additional request.
 */
export const useLicenseGate = ({ scope, id }: LicenseGateScope): LicenseGate | null => {
  const { getAccessTokenSilently } = useAuth0();
  const tenantId = useAppSelector(state => state.selection?.tenantId);
  const [gate, setGate] = useState<LicenseGate | null>(null);

  useEffect(() => {
    const numericId = Number(id);
    const usableId =
      Boolean(id) && (scope === 'instanceBySubId' || !Number.isNaN(numericId));
    if (!usableId || !tenantId) {
      setGate(null);
      return;
    }

    // Guards against a late response from a previously selected reflector overwriting the
    // verdict for the one now on screen.
    let active = true;

    const resolve = async () => {
      try {
        const token = await getAccessTokenSilently();

        if (scope === 'cluster') {
          const res = await getClusterLicenseValidity(token, numericId, tenantId);
          const data = res.data?.data;
          if (!active) return;
          setGate(
            data && data.valid === false
              ? {
                  subject: clusterSubject(data.members ?? []),
                  reason: data.reason,
                  endDate:
                    (data.members ?? []).find(member => !member.valid)?.license?.endDate ?? null
                }
              : null
          );
          return;
        }

        // A subId has to become a numeric id first: the validity endpoint is keyed on the
        // database id, and only the reflectors list carries both.
        let reflectorId = numericId;
        if (scope === 'instanceBySubId') {
          const list = await getReflectorsList(token, tenantId);
          const rows: Array<{ id?: number; subId?: string }> = Array.isArray(list.data)
            ? list.data
            : (list.data?.data ?? []);
          const match = rows.find(row => row.subId === id);
          if (!active) return;
          if (!match?.id) {
            // Unknown subId — fail open rather than guess.
            setGate(null);
            return;
          }
          reflectorId = match.id;
        }

        const res = await getReflectorLicenseValidity(token, reflectorId, tenantId);
        const data = res.data?.data;
        if (!active) return;
        setGate(
          data && data.valid === false
            ? {
                subject: data.name || data.subId || `Reflector ${data.reflectorId}`,
                reason: data.reason,
                endDate: data.license?.endDate ?? null
              }
            : null
        );
      } catch {
        // Fail open — see the docblock.
        if (active) {
          setGate(null);
        }
      }
    };

    resolve();

    return () => {
      active = false;
    };
  }, [scope, id, tenantId, getAccessTokenSilently]);

  return gate;
};

export default useLicenseGate;
